import { useMemo } from 'react';
import { useSelector } from 'react-redux';

/**
 * Custom hook to merge data and comparisonData arrays
 * Ensures all items from both arrays are included in the result
 * Items that exist only in comparisonData will have 0 values for main data properties
 * Only merges when rangesSet is true from comparison state
 * 
 * @param {Array} data - Main data array
 * @param {Array} comparisonData - Comparison data array
 * @returns {Array} Merged array containing all unique items from both arrays
 */
const useMergedData = (data, comparisonData) => {
	const { rangesSet } = useSelector((state) => state.comparison);
	const mergedData = useMemo(() => {
		// Only merge data when rangesSet is true
		if (!rangesSet) {
			return data || [];
		}
		
		if (!data && !comparisonData) return [];
		if (!comparisonData) return data || [];
		if (!data) return comparisonData?.map(compItem => ({
			_id: compItem._id,
			label: compItem.label || `Item ${compItem._id}`,
			total: 0,
			percentage: 0,
		})) || [];
		
		// Start with existing data items
		const dataMap = new Map(data.map(item => [item._id, item]));
		
		// Add comparison items that don't exist in data
		comparisonData.forEach(compItem => {
			if (!dataMap.has(compItem._id)) {
				// Create a data item with 0 values for comparison items not in data
				dataMap.set(compItem._id, {
					_id: compItem._id,
					label: compItem.label || `Item ${compItem._id}`,
					total: 0,
					percentage: 0,
					patientIds : [],
					ids: [],
					facilityIds: [],
					facilityNames: [],
					isReadOnly: true					
				});
			}
		});
		
		return Array.from(dataMap.values());
	}, [data, comparisonData, rangesSet]);

	return mergedData;
};

export default useMergedData; 